import type { ColumnDef } from "@tanstack/react-table";
import { ArrowLeft, Lock } from "lucide-react";
import { useMemo, useState } from "react";
import { Navigate, useNavigate, useParams } from "react-router-dom";
import { FeedbackBanner } from "@/core/components/feedback-banner";
import { LoadingSpinner } from "@/core/components/loading-spinner";
import { SimpleTable } from "@/core/components/simple-table";
import { Button } from "@/core/components/ui/button";
import {
  SimpleSidebar,
  SimpleSidebarContent,
  SimpleSidebarLayout,
} from "@/core/components/ui/simple-sidebar";
import { Stack } from "@/core/components/ui/stack";
import { toast } from "@/core/components/ui/toast";
import { Typography } from "@/core/components/ui/typography";
import { getErrorMessage } from "@/core/api/errors";
import useAuth from "@/core/auth/use-auth";
import { useBusiness } from "@/core/business";
import { cn } from "@/core/utils/utils";
import { PayBreakdownPaneShell } from "@/payroll/pay-breakdown/pay-breakdown-pane-shell";
import { usePayrollEmailsAccess } from "@/payroll/pay-breakdown/payroll-emails-access";
import { getPayBreakdownPath } from "@/payroll/pay-breakdown/routes";
import {
  PAYROLL_EMAILS_ACCESS,
  type PayrollEmailsAccess,
} from "@/payroll/pay-breakdown/types";
import { isTech } from "@/user/utils";
import { ChangedPacksBanner } from "./changed-packs-banner";
import {
  ApprovalCol,
  EmailCol,
  FinalPayCol,
  NoteCol,
  SelectCol,
  StatusCol,
  TechnicianCol,
} from "./cols";
import { DeliveryPollingBanner } from "./delivery-polling-banner";
import {
  PacksHeaderActions,
  PacksSectionProvider,
  PacksSidebar,
  usePacksSection,
} from "./packs-section";
import { ProjectionBuildBanner } from "./projection-build-banner";
import { ProjectionBuildLoadingState } from "./projection-build-loading-state";
import { RecipientEmptyState } from "./recipient-empty-state";
import { SendConfirmationDialog } from "./send-confirmation-dialog";
import { SendFailuresBanner } from "./send-failures-banner";
import { SIDEBAR_VIEW } from "./sidebar-view";
import type {
  PayrollStatementNotificationChannel,
  PayrollStatementNotificationRecipient,
} from "./types";
import {
  getNotificationReasonLabel,
  getTextableRecipientsCount,
  getTextOnlyReach,
  isRenotifyOnly,
  summarizePackApprovals,
} from "./utils";

interface PayrollStatementNotificationsViewProps {
  businessId: string | null;
  periodStart: string | null;
  // When embedded in the "PayPacks" tab we drop the page chrome (the
  // "Back to Grid" button) and fill the tab's height instead of the full
  // page. The route page renders this with embedded=false.
  embedded?: boolean;
  payrollEmailsAccess: PayrollEmailsAccess;
  /**
   * Technicians with an open PackChat thread about this pack. They are marked and
   * sorted to the top, so a manager can see who is waiting without reading every
   * row. The questions themselves live on PackChat.
   */
  awaitingReplyTechnicianIds?: Set<string>;
}

// The payroll statement-emails feature, extracted from the route page so it can
// be mounted inline in the "PayPacks" tab as well as on its own route.
export const PayrollStatementNotificationsContent = ({
  embedded = false,
}: {
  // When embedded in the "PayPacks" tab we drop the page chrome (the
  // "Back to Grid" button), and the section's controls and sidebar are rendered
  // by the page from the same provider rather than inline here.
  embedded?: boolean;
}) => {
  const navigate = useNavigate();
  // How the next send notifies technicians, for a business that texts PayPacks.
  // Kept across sends on the page, so a manager who texts only needn't untick
  // email every time.
  const [channels, setChannels] = useState<
    PayrollStatementNotificationChannel[]
  >(["email", "text"]);
  const {
    awaitingReplyTechnicianIds,
    canUsePayrollEmails,
    previewOnly,
    data,
    error,
    projectionBuild,
    canRetryProjectionHydration,
    isProjectionRefreshing,
    refetchProjectionBuild,
    retryProjectionBuild,
    isRetryingProjection,
    recipients,
    filteredRecipients,
    selectedRecipientIds,
    eligibleRecipients,
    selectedEligibleRecipients,
    allEligibleSelected,
    someEligibleSelected,
    toggleRecipient,
    toggleAllEligible,
    noteDrafts,
    detailTechnicianId,
    detailRecipient,
    openDetail,
    closeDetail,
    teamIds,
    setTeamIds,
    technicianSearch,
    setTechnicianSearch,
    periodLabel,
    sendDisabledReason,
    send,
    sendFailures,
    isSending,
    isSavingNote,
    deliveryPoll,
    missingReadyForEmailAccessKeys,
    isPayPacksLoading,
    canShowRecipients,
    showRecipientContent,
    isProjectionBuilding,
    confirmSendOpen,
    setConfirmSendOpen,
    periodStart,
    changedRecipients,
    blockedChangedRecipients,
    selectChangedPacks,
  } = usePacksSection();

  const approvals = summarizePackApprovals(filteredRecipients);
  const showApprovals =
    !previewOnly &&
    approvals.approved + approvals.stale + approvals.unknown > 0;

  const columns = useMemo<
    ColumnDef<PayrollStatementNotificationRecipient, unknown>[]
  >(
    () => [
      SelectCol({
        disabled: previewOnly,
        selectedRecipientIds,
        allEligibleSelected,
        someEligibleSelected,
        eligibleRecipientsCount: eligibleRecipients.length,
        onToggleAllEligible: toggleAllEligible,
        onToggleRecipient: toggleRecipient,
      }),
      TechnicianCol(awaitingReplyTechnicianIds),
      EmailCol(),
      FinalPayCol({ isBuilding: isProjectionBuilding }),
      // Sign-off rides on the PayPack rollout. Off it there are no approvals, so a
      // column of "Not yet" would advertise a feature this business does not have —
      // and rows can outlive the flag being switched off.
      ...(previewOnly ? [] : [ApprovalCol()]),
      StatusCol(),
      NoteCol({ noteDrafts }),
    ],
    [
      selectedRecipientIds,
      allEligibleSelected,
      someEligibleSelected,
      eligibleRecipients.length,
      noteDrafts,
      previewOnly,
      isProjectionBuilding,
      awaitingReplyTechnicianIds,
      toggleAllEligible,
      toggleRecipient,
    ],
  );

  return (
    <SimpleSidebarLayout
      open={!embedded && !!detailRecipient}
      onOpenChange={(open) => {
        if (!open) closeDetail();
      }}
      sidebarWidth="420px"
      className={cn({ "!h-full": embedded })}
    >
      <SimpleSidebarContent removeScrollArea>
        <PayBreakdownPaneShell
          fillHeight
          gap="lg"
          variant={embedded ? "pane" : "page"}
          className="h-full"
        >
          {/* Standalone route chrome only — back link + title. When embedded in the
              "PayPacks" tab, the period header + active tab already say
              this, and the Send action lives in the recipients pane header below. */}
          {!embedded && (
            <Stack gap="xs" className="min-w-0 shrink-0">
              <Button
                variant="text"
                size="xs"
                className="w-fit px-0"
                leftIcon={<ArrowLeft className="h-4 w-4" />}
                onClick={() =>
                  navigate(getPayBreakdownPath(periodStart ?? undefined))
                }
              >
                Back to Grid
              </Button>
              <Stack
                direction="row"
                align="baseline"
                gap="sm"
                className="flex-wrap"
              >
                <Typography variant="titleW3">PayPacks</Typography>
                <Typography variant="bodyW3" textColor="secondary">
                  {periodLabel}
                </Typography>
              </Stack>
            </Stack>
          )}

          {error && !data && (
            <FeedbackBanner variant="error">
              <Typography variant="bodyW3">
                Couldn't load this period.
              </Typography>
              <Typography variant="bodyW2" textColor="secondary">
                {getErrorMessage(error)}
              </Typography>
            </FeedbackBanner>
          )}

          {canShowRecipients &&
            data &&
            !data.can_send &&
            canUsePayrollEmails && (
              <FeedbackBanner variant="warning">
                <Typography variant="bodyW3">
                  These emails aren't ready to send yet.
                </Typography>
                <Typography variant="bodyW2" textColor="secondary">
                  {getNotificationReasonLabel(data.cannot_send_reason) ??
                    "This period can't be published yet."}
                </Typography>
              </FeedbackBanner>
            )}

          <ProjectionBuildBanner
            build={projectionBuild}
            canCheckAgain={canRetryProjectionHydration}
            isRefreshing={isProjectionRefreshing}
            isRetrying={isRetryingProjection}
            onCheckAgain={() => {
              void refetchProjectionBuild();
            }}
            onRetry={() => {
              retryProjectionBuild(undefined, {
                onError: (retryError) => {
                  toast.error("Couldn't retry the PayPack build", {
                    description: getErrorMessage(retryError),
                  });
                },
              });
            }}
          />

          {isPayPacksLoading && <ProjectionBuildLoadingState />}

          {showRecipientContent && (
            <>
              <DeliveryPollingBanner
                error={deliveryPoll.error}
                pollingTimedOut={!!deliveryPoll.data?.polling_timed_out}
                isRefreshing={deliveryPoll.isFetching}
                onCheckAgain={() => {
                  void deliveryPoll.refetch();
                }}
              />

              <SendFailuresBanner
                deliveries={sendFailures}
                recipients={recipients}
              />

              {/* Above the table rather than in the header: it is a finding about
                  this period, not a control, and it names the technicians it is
                  about. */}
              <ChangedPacksBanner
                changedRecipients={changedRecipients}
                blockedRecipients={blockedChangedRecipients}
                onSelect={selectChangedPacks}
                disabled={previewOnly}
              />

              <Stack
                gap="none"
                className="min-h-0 w-full flex-1 overflow-hidden rounded-lg border border-stroke-tertiary bg-surface-primary"
              >
                {/* Send action lives in the pane header (not floating above it), so the
                recipients list reads as one unit. Hidden when there's nothing to send. */}
                {recipients.length > 0 && (
                  <Stack
                    direction="row"
                    align="center"
                    justify="between"
                    gap="md"
                    fullWidth
                    className="shrink-0 flex-wrap gap-y-3 border-b border-stroke-tertiary px-4 py-3"
                  >
                    <Stack gap="xs" className="min-w-0">
                      <Stack direction="row" align="center" gap="xs">
                        {previewOnly && (
                          <Lock
                            className="size-3.5 shrink-0 text-tertiary-text"
                            aria-hidden
                          />
                        )}
                        <Typography variant="bodyW3" textColor="secondary">
                          {previewOnly
                            ? "PayPack emails aren't set up for this business."
                            : `${filteredRecipients.length} recipient${filteredRecipients.length === 1 ? "" : "s"}`}
                          {showApprovals && (
                            <>
                              {` · ${approvals.approved} approved`}
                              {approvals.stale > 0 &&
                                `, ${approvals.stale} before the figures changed`}
                              {approvals.unknown > 0 &&
                                `, ${approvals.unknown} unverified`}
                            </>
                          )}
                        </Typography>
                      </Stack>
                      <Typography variant="captionW1" textColor="tertiary">
                        {technicianSearch.trim() &&
                          `${filteredRecipients.length} search match${filteredRecipients.length === 1 ? "" : "es"}. `}
                        Click a recipient to view their PayPack.
                      </Typography>
                    </Stack>
                    {!embedded && <PacksHeaderActions />}
                  </Stack>
                )}
                <Stack
                  className={cn("min-h-0 w-full flex-1 overflow-hidden", {
                    "opacity-60": previewOnly,
                  })}
                >
                  {!error && filteredRecipients.length === 0 ? (
                    <RecipientEmptyState
                      hasTeamFilter={teamIds.length > 0}
                      onClearTeams={() => setTeamIds([])}
                      searchValue={technicianSearch}
                      onClearSearch={() => setTechnicianSearch("")}
                    />
                  ) : (
                    <SimpleTable
                      data={filteredRecipients}
                      columns={columns}
                      getRowId={(recipient) => recipient.technician_id}
                      onRowClick={(recipient) =>
                        openDetail(recipient, SIDEBAR_VIEW.preview)
                      }
                      selectedRowId={detailTechnicianId}
                      className="h-full min-h-0 w-full min-w-0"
                      tableClassName="min-w-[1080px]"
                      columnPinning={{ left: ["select"] }}
                    />
                  )}
                </Stack>
              </Stack>
            </>
          )}
        </PayBreakdownPaneShell>
      </SimpleSidebarContent>

      <SimpleSidebar>{!embedded && <PacksSidebar />}</SimpleSidebar>

      {canUsePayrollEmails && (
        <SendConfirmationDialog
          open={confirmSendOpen}
          isSending={isSending}
          isSavingNote={isSavingNote}
          sendDisabledReason={sendDisabledReason}
          periodLabel={periodLabel}
          selectedEligibleCount={selectedEligibleRecipients.length}
          renotify={isRenotifyOnly(selectedEligibleRecipients)}
          eligibleCount={eligibleRecipients.length}
          textCount={
            data?.sms_enabled
              ? getTextableRecipientsCount(selectedEligibleRecipients)
              : null
          }
          {...getTextOnlyReach(selectedEligibleRecipients)}
          channels={data?.sms_enabled ? channels : null}
          onChannelsChange={setChannels}
          missingReadyForEmailAccessKeys={missingReadyForEmailAccessKeys}
          onOpenChange={setConfirmSendOpen}
          onSend={async () => {
            if (await send(data?.sms_enabled ? channels : null))
              setConfirmSendOpen(false);
          }}
        />
      )}
    </SimpleSidebarLayout>
  );
};

export const PayrollStatementNotificationsView = ({
  embedded,
  ...input
}: PayrollStatementNotificationsViewProps) => (
  <PacksSectionProvider
    key={`${input.businessId ?? ""}:${input.periodStart ?? ""}`}
    {...input}
  >
    <PayrollStatementNotificationsContent embedded={embedded} />
  </PacksSectionProvider>
);

// Route page: resolves business + period from context/params, keeps the
// technician redirect, and renders the shared view with full page chrome.
const PayrollStatementNotificationsPage = () => {
  const { periodStart } = useParams<{ periodStart: string }>();
  const { businessId } = useBusiness();
  const { user } = useAuth();
  const { payrollEmailsAccess, isLoading } = usePayrollEmailsAccess();

  if (isTech(user?.role)) {
    return <Navigate to={getPayBreakdownPath(periodStart)} replace />;
  }

  if (isLoading) return <LoadingSpinner />;

  if (payrollEmailsAccess === PAYROLL_EMAILS_ACCESS.hidden) {
    return <Navigate to={getPayBreakdownPath(periodStart)} replace />;
  }

  return (
    <PayrollStatementNotificationsView
      businessId={businessId}
      periodStart={periodStart ?? null}
      payrollEmailsAccess={payrollEmailsAccess}
    />
  );
};

export default PayrollStatementNotificationsPage;
